<script setup lang="ts">
import {computed, type Component} from "vue";
import {ClipboardList, HeartPulse, House, Pill, Sparkles} from "@lucide/vue";
import {
    InterviewSheetAnswer,
    InterviewSheetAnswers,
    InterviewSheetIcon,
    InterviewSheetLine,
    InterviewSheetQuestion,
    InterviewSheetView
} from "@/medication-checkup/interfaces/interview-sheet";
import {CARD_SHEET, FLAGGED, pillClasses} from "@/medication-checkup/services/SceneClasses";
import {asksYesNo, opensFollowUp, takesChoices} from "@/medication-checkup/services/InterviewSheetRules";
import MedicationCheckupYesNo from "@/medication-checkup/components/MedicationCheckupYesNo.vue";

/**
 * The card of one part of the interview sheet: its questions, each asking a
 * yes/no, a yes/no with a free text on « yes », a free text alone, or
 * options to pick — some opening a follow-up list with « Autre » —, under
 * the sub-groups the part declares. A « yes » may name the retained
 * treatments where the question asks it.
 *
 * Answering is local to the scene: the card shows the answers it is handed
 * while live — the pharmacist's over the collection's proposals —, the ones
 * the server froze once the block is completed, each pre-filled one wearing
 * its badge.
 */

const props = defineProps<{
    view: InterviewSheetView;
    frozen?: boolean;
    /**
     * The answers given so far, while the part is live.
     */
    answers?: InterviewSheetAnswers;
    /**
     * The refs of the questions the scene points at as unanswered.
     */
    flagged?: string[];
}>();

const emit = defineEmits<{
    answer: [ref: string, answer: InterviewSheetAnswer];
}>();

/**
 * The icon of a part, by the key the handler serves.
 */
const ICONS: Record<InterviewSheetIcon, Component> = {
    habits: House,
    physiology: HeartPulse,
    treatments: Pill,
};

const icon = computed(() => ICONS[props.view.icon] ?? ClipboardList);

/**
 * The questions in order, each with the sub-group it opens — the served group
 * repeats on every question of the group, the card titles it once.
 */
const rows = computed(() => {
    return props.view.questions.map((question, index) => ({
        question,
        index,
        heading: question.group && (index === 0 || question.group !== props.view.questions[index - 1].group) ? question.group : null,
    }));
});

/**
 * What a question reads as answered: the local answer while live, the one
 * the server froze once the block is completed.
 */
const answerOf = (question: InterviewSheetQuestion): InterviewSheetAnswer | null => {
    return props.frozen ? question.answer : props.answers?.[question.ref] ?? null;
};

/**
 * The words typed for « Autre » under a question answered by picking.
 */
const OTHER = 'Autre';

/**
 * Whether the text field of a question shows: always on a free text
 * question, on a « yes » otherwise. Frozen, the field stays as typed when a
 * text was given, disabled.
 */
const showsText = (question: InterviewSheetQuestion): boolean => {
    return question.type === 'text' || (question.type === 'yn_text' && answerOf(question)?.yes_no === true);
};

/**
 * What the badge of a pre-filled question says, or nothing for an answer the
 * pharmacist gave alone. The badge names no source: a proposal may come from
 * the interview as well as from the dispensing history.
 */
const prefilled = (question: InterviewSheetQuestion): string | null => {
    switch (question.answer?.provenance) {
        case 'proposed':
            return props.frozen ? null : 'Pré-rempli';
        case 'accepted':
            return props.frozen ? 'Pré-rempli' : null;
        case 'edited':
            return props.frozen ? 'Pré-rempli, modifié' : null;
        default:
            return null;
    }
};

/**
 * What a question reads as answered, with nothing picked, typed or named
 * where nothing was.
 */
const given = (question: InterviewSheetQuestion): Required<InterviewSheetAnswer> => {
    const answer = answerOf(question);

    return {
        yes_no: answer?.yes_no ?? null,
        text: answer?.text ?? null,
        choices: answer?.choices ?? [],
        other: answer?.other ?? null,
        line_ids: answer?.line_ids ?? [],
    };
};

const update = (question: InterviewSheetQuestion, changes: Partial<InterviewSheetAnswer>) => {
    emit('answer', question.ref, {...given(question), ...changes});
};

/**
 * Say yes — keeping what was typed, picked and named, bringing the proposed
 * text back when nothing was typed.
 */
const pickYes = (question: InterviewSheetQuestion) => {
    update(question, {yes_no: true, text: answerOf(question)?.text ?? question.answer?.text ?? null});
};

/**
 * Say no — dropping what only rides on a « yes ».
 */
const pickNo = (question: InterviewSheetQuestion) => {
    update(question, {yes_no: false, text: null, choices: [], other: null, line_ids: []});
};

const typed = (question: InterviewSheetQuestion, event: Event) => {
    update(question, {text: (event.target as HTMLInputElement).value});
};

/**
 * A question answered by picking, frozen on words given before it took
 * options: its text reads as it was written.
 */
const writtenBefore = (question: InterviewSheetQuestion): boolean => {
    return !!props.frozen && given(question).choices.length === 0 && given(question).other === null && !!question.answer?.text;
};

const picked = (question: InterviewSheetQuestion, option: string): boolean => given(question).choices.includes(option);

/**
 * Pick an option, or drop it. Dropping the option that opens the follow-up
 * drops what was picked and typed in it.
 */
const toggleChoice = (question: InterviewSheetQuestion, option: string) => {
    const choices = given(question).choices;

    if (!choices.includes(option)) {
        update(question, {choices: [...choices, option]});

        return;
    }

    const closes = option === question.follow_up?.trigger;
    const followUp = question.follow_up?.options ?? [];

    update(question, {
        choices: choices.filter(choice => choice !== option && !(closes && followUp.includes(choice))),
        ...closes ? {other: null} : {}
    });
};

/**
 * Pick « Autre », opening its field blank, or drop it with what was typed.
 */
const toggleOther = (question: InterviewSheetQuestion) => {
    update(question, {other: given(question).other === null ? '' : null});
};

const typedOther = (question: InterviewSheetQuestion, event: Event) => {
    update(question, {other: (event.target as HTMLInputElement).value});
};

/**
 * The treatments a « yes » may name: every retained one while live, the ones
 * it named once frozen.
 */
const lines = (question: InterviewSheetQuestion): InterviewSheetLine[] => {
    const all = props.view.lines ?? [];

    return props.frozen ? all.filter(line => given(question).line_ids.includes(line.id)) : all;
};

const showsLines = (question: InterviewSheetQuestion): boolean => {
    return !!question.lines && given(question).yes_no === true && lines(question).length > 0;
};

const toggleLine = (question: InterviewSheetQuestion, line: InterviewSheetLine) => {
    const named = given(question).line_ids;

    update(question, {line_ids: named.includes(line.id) ? named.filter(id => id !== line.id) : [...named, line.id]});
};

const CHOICE = 'px-3 py-1 rounded-full border-[1.5px] border-dashed text-xs font-medium disabled:opacity-60';
</script>

<template>
  <div
    :class="CARD_SHEET"
    class="max-w-[640px] rounded-xl overflow-hidden"
  >
    <div class="flex items-center gap-2 px-4 py-3 border-b border-gray-light">
      <component :is="icon" class="w-[18px] h-[18px] text-primary" />
      <span class="text-sm font-semibold text-primary">{{ view.title }}</span>
      <span class="ml-auto shrink-0 px-2.5 py-0.5 rounded-full bg-primary-lightest text-primary text-xs font-semibold">
        Partie {{ view.part.rank }}/{{ view.part.total }}
      </span>
    </div>

    <div class="flex flex-col gap-4 px-4 py-4">
      <div
        v-for="row in rows"
        :key="row.question.ref"
        class="flex flex-col gap-1.5 -mx-2 -my-1 px-2 py-1 animate-message-in"
        :class="{[FLAGGED]: flagged?.includes(row.question.ref)}"
        :style="{animationDelay: `${row.index * 60}ms`}"
        :data-question-ref="row.question.ref"
      >
        <span
          v-if="row.heading"
          class="text-xs font-bold uppercase tracking-wider text-muted-foreground"
          :class="{'mt-2 pt-3 border-t border-gray-light': row.index > 0}"
        >
          {{ row.heading }}
        </span>

        <div class="flex items-center gap-2.5">
          <span class="flex-1 flex flex-col gap-0.5">
            <span class="text-sm font-semibold leading-snug">{{ row.question.text }}</span>
            <span v-if="row.question.hint" class="text-xs italic text-muted-foreground">{{ row.question.hint }}</span>
          </span>

          <MedicationCheckupYesNo
            v-if="asksYesNo(row.question)"
            :answer="answerOf(row.question)?.yes_no ?? null"
            :disabled="frozen"
            @answer="given => given ? pickYes(row.question) : pickNo(row.question)"
          />
        </div>

        <template v-if="showsText(row.question)">
          <input
            v-if="!frozen || answerOf(row.question)?.text"
            :value="answerOf(row.question)?.text ?? ''"
            :placeholder="row.question.placeholder ?? ''"
            :disabled="frozen"
            class="w-full px-2.5 py-1.5 rounded-lg border border-solid border-slate-200 bg-white text-sm outline-none focus:border-primary disabled:text-gray-dark"
            @input="typed(row.question, $event)"
          >
          <span v-else-if="row.question.type === 'text'" class="text-sm italic text-muted-foreground">
            Non renseigné
          </span>
        </template>

        <template v-if="takesChoices(row.question)">
          <span v-if="writtenBefore(row.question)" class="text-sm text-gray-dark">{{ row.question.answer?.text }}</span>

          <template v-else>
            <div v-if="row.question.options?.length" class="flex flex-wrap gap-1.5">
              <button
                v-for="option in row.question.options"
                :key="option"
                :class="[CHOICE, pillClasses(picked(row.question, option))]"
                :disabled="frozen"
                @click="toggleChoice(row.question, option)"
              >
                {{ option }}
              </button>
            </div>

            <div
              v-if="row.question.follow_up && opensFollowUp(row.question, given(row.question))"
              class="flex flex-wrap items-center gap-1.5 animate-message-in"
            >
              <span class="text-xs text-muted-foreground">{{ row.question.follow_up.label }} :</span>
              <button
                v-for="option in row.question.follow_up.options"
                :key="option"
                :class="[CHOICE, pillClasses(picked(row.question, option))]"
                :disabled="frozen"
                @click="toggleChoice(row.question, option)"
              >
                {{ option }}
              </button>
              <button
                :class="[CHOICE, pillClasses(given(row.question).other !== null)]"
                :disabled="frozen"
                @click="toggleOther(row.question)"
              >
                {{ OTHER }}
              </button>
            </div>

            <input
              v-if="given(row.question).other !== null"
              :value="given(row.question).other ?? ''"
              :placeholder="row.question.placeholder ?? ''"
              :disabled="frozen"
              class="w-full px-2.5 py-1.5 rounded-lg border border-solid border-slate-200 bg-white text-sm outline-none focus:border-primary disabled:text-gray-dark"
              @input="typedOther(row.question, $event)"
            >
          </template>
        </template>

        <div v-if="showsLines(row.question)" class="flex flex-wrap items-center gap-1.5">
          <span class="text-xs text-muted-foreground">Traitements concernés (facultatif) :</span>
          <button
            v-for="line in lines(row.question)"
            :key="line.id"
            class="px-2.5 py-0.5 rounded-full border-[1.5px] border-solid text-xs font-medium disabled:opacity-60"
            :class="pillClasses(given(row.question).line_ids.includes(line.id))"
            :disabled="frozen"
            @click="toggleLine(row.question, line)"
          >
            {{ line.label }}
          </button>
        </div>

        <span
          v-if="prefilled(row.question)"
          class="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full bg-primary-lightest text-primary text-[10px] font-bold"
        >
          <Sparkles class="w-3 h-3" />
          {{ prefilled(row.question) }}
        </span>
      </div>
    </div>
  </div>
</template>
